<template>
  <el-dialog
    title="积分充值"
    :visible.sync="dialogVisible"
    width="420px"
    :close-on-click-modal="false"
    custom-class="points-recharge-dialog"
    @close="handleClose">
    <div class="recharge-content">
      <!-- 当前余额 -->
      <div class="balance-section">
        <div class="balance-label">当前积分余额</div>
        <div class="balance-value">
          <i class="el-icon-coin"></i>
          <span>{{ currentBalance }}</span>
        </div>
      </div>

      <!-- 充值选项 -->
      <div class="recharge-options">
        <div class="options-title">选择充值数量</div>
        <div class="options-grid">
          <div
            v-for="option in rechargeOptions"
            :key="option.points"
            class="option-card"
            :class="{ active: selectedOption === option.points }"
            @click="selectOption(option.points)">
            <div class="option-points">{{ option.points }}</div>
            <div class="option-label">积分</div>
            <div class="option-price">¥{{ option.price }}</div>
          </div>
        </div>
      </div>

      <!-- 自定义数量 -->
      <div class="custom-section">
        <div class="custom-label">或输入自定义数量</div>
        <el-input-number
          v-model="customAmount"
          :min="0"
          :max="99999"
          :step="10"
          @change="handleCustomChange">
        </el-input-number>
        <span class="custom-price" v-if="customAmount > 0">
          ≈ ¥{{ (customAmount * 0.1).toFixed(2) }}
        </span>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        @click="handleRecharge"
        :loading="loading"
        :disabled="!selectedAmount">
        <i class="el-icon-coin" v-if="!loading"></i>
        充值 {{ selectedAmount || 0 }} 积分
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getPointsBalance, rechargePoints } from '@/api/points'
import { getTokenInfo } from '@/utils/jwtUtils'

export default {
  name: 'PointsRechargeDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      currentBalance: 0,
      selectedOption: null,
      customAmount: 0,
      rechargeOptions: [
        { points: 100, price: '10.00' },
        { points: 500, price: '50.00' },
        { points: 1000, price: '100.00' },
        { points: 2000, price: '200.00' }
      ]
    }
  },
  computed: {
    selectedAmount() {
      if (this.selectedOption != null) return this.selectedOption
      if (this.customAmount != null && this.customAmount > 0) return this.customAmount
      return null
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.loadBalance()
        this.selectedOption = null
        this.customAmount = 0
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    async loadBalance() {
      try {
        const tokenInfo = getTokenInfo()
        if (!tokenInfo || !tokenInfo.id) return

        const res = await getPointsBalance(tokenInfo.id)
        if (res && res.code === 1) {
          this.currentBalance = res.data || 0
        }
      } catch (e) {
        console.error('获取积分余额失败:', e)
      }
    },

    selectOption(points) {
      this.selectedOption = points
      this.customAmount = 0
    },

    handleCustomChange() {
      if (this.customAmount && this.customAmount > 0) {
        this.selectedOption = null
      }
    },

    async handleRecharge() {
      if (!this.selectedAmount) {
        this.$message.warning('请选择充值数量')
        return
      }

      this.loading = true
      try {
        const tokenInfo = getTokenInfo()
        if (!tokenInfo || !tokenInfo.id) {
          this.$message.error('请先登录')
          this.loading = false
          return
        }

        const res = await rechargePoints({
          userId: tokenInfo.id,
          username: tokenInfo.userName || tokenInfo.username,
          amount: this.selectedAmount,
          remark: '积分充值'
        })

        if (res && res.code === 1) {
          this.$message.success(`充值成功！获得 ${this.selectedAmount} 积分`)
          this.currentBalance = res.data.balance
          this.$emit('recharged', res.data)
          this.dialogVisible = false
        } else {
          this.$message.error(res.msg || '充值失败')
        }
      } catch (e) {
        console.error('充值失败:', e)
        this.$message.error('充值失败，请重试')
      } finally {
        this.loading = false
      }
    },

    handleClose() {
      this.dialogVisible = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.recharge-content {
  padding: 10px 0;
}

.balance-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  border-radius: 12px;
  color: #333;
  margin-bottom: 20px;
}

.balance-label {
  font-size: 14px;
}

.balance-value {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 28px;
  font-weight: 700;
}

.balance-value i {
  font-size: 24px;
}

.recharge-options {
  margin-bottom: 20px;
}

.options-title {
  font-size: 14px;
  color: #606266;
  margin-bottom: 12px;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.option-card {
  padding: 16px 8px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.option-card:hover {
  border-color: #409eff;
}

.option-card.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.option-points {
  font-size: 20px;
  font-weight: 700;
  color: #303133;
}

.option-label {
  font-size: 12px;
  color: #909399;
  margin: 4px 0;
}

.option-price {
  font-size: 14px;
  color: #f56c6c;
  font-weight: 500;
}

.custom-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.custom-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.custom-price {
  font-size: 14px;
  color: #f56c6c;
  white-space: nowrap;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.dialog-footer .el-button i {
  margin-right: 4px;
}
</style>
